import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  MessageSquare,
  Eye,
  Shield,
  Key,
  Mail,
  Tag,
  Megaphone,
  DollarSign,
  Truck,
} from 'lucide-react';
import { useAdminStoreView } from '../context/AdminStoreViewContext';
import BatchtrueMark from './assay/BatchtrueMark';

// Shared nav definition — also consumed by AdminMobileTopBar.
export const adminNavItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', group: 'Overview' },
  { path: '/admin/orders', icon: ShoppingCart, label: 'Orders', group: 'Orders' },
  { path: '/admin/fulfillment', icon: Truck, label: 'Fulfillment', group: 'Orders' },
  { path: '/admin/customers', icon: Users, label: 'Customers', group: 'Customers' },
  { path: '/admin/products', icon: Package, label: 'Products', group: 'Catalog' },
  { path: '/admin/tickets', icon: MessageSquare, label: 'Tickets', group: 'Support' },
  { path: '/admin/invite-codes', icon: Key, label: 'Invite Codes', group: 'Access and communications' },
  { path: '/admin/benefit-codes', icon: Tag, label: 'Benefit Codes', group: 'Access and communications' },
  { path: '/admin/announcements', icon: Megaphone, label: 'Announcements', group: 'Access and communications' },
  { path: '/admin/email-templates', icon: Mail, label: 'Email Templates', group: 'Access and communications' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', group: 'Reports and settings' },
  { path: '/admin/sales', icon: DollarSign, label: 'Sales', group: 'Reports and settings' },
  { path: '/admin/settings', icon: Shield, label: 'Settings', group: 'Reports and settings' },
];

// Resolves the active label for the current admin pathname.
export function resolveAdminPageTitle(pathname) {
  // Prefer exact match first; then longest startsWith for nested routes.
  const exact = adminNavItems.find((n) => n.path === pathname);
  if (exact) return exact.label;
  const startsMatch = adminNavItems
    .filter((n) => n.path !== '/admin' && pathname.startsWith(n.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return startsMatch?.label || 'Admin';
}

/**
 * Renders the full vertical nav list. Shared between the fixed desktop sidebar
 * and the mobile slide-in Sheet so both stay in lockstep.
 */
export function AdminNavList({ onNavigate, idPrefix = 'admin-nav' }) {
  const location = useLocation();

  return (
    <nav className="min-h-0 flex-1 overflow-y-auto p-4 space-y-1" aria-label="Admin navigation">
      {adminNavItems.map((item, index) => {
        const isActive =
          location.pathname === item.path ||
          (item.path !== '/admin' && location.pathname.startsWith(item.path));
        const showGroup = index === 0 || adminNavItems[index - 1].group !== item.group;
        return (
          <React.Fragment key={item.path}>
            {showGroup ? (
              <p className="label-quiet px-3 pb-1 pt-3 font-mono first:pt-0">
                {item.group}
              </p>
            ) : null}
            <Link
            to={item.path}
            onClick={onNavigate}
            data-testid={`${idPrefix}-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center gap-3 px-3 rounded-lg text-sm transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              isActive
                ? 'bg-primary text-primary-foreground font-medium'
                : 'text-foreground hover:bg-accent hover:text-primary'
            }`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span>{item.label}</span>
            </Link>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/**
 * Brand header (logo mark + product name) used at the top of both the desktop
 * sidebar and the mobile drawer.
 */
export function AdminBrandHeader({ className = '' }) {
  return (
    <div className={`shrink-0 p-6 border-b border-border ${className}`}>
      <div className="flex items-center gap-3">
        <BatchtrueMark className="h-10 w-10 flex-shrink-0 text-primary" />
        <div>
          <span className="font-display font-semibold text-sm tracking-tight text-foreground">
            Batchtrue Wholesale
          </span>
          <p className="text-xs text-muted-foreground">Admin Panel</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Customer view entry, shared by desktop and mobile navigation.
 */
export function AdminBackToStoreLink({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { enterStoreView } = useAdminStoreView();

  const handleStoreView = () => {
    const didEnter = enterStoreView(location.pathname);
    onNavigate?.();
    if (didEnter) navigate('/catalog');
  };

  return (
    <div className="shrink-0 p-4 border-b border-border">
      <button
        type="button"
        onClick={handleStoreView}
        data-testid="admin-nav-back-to-store"
        className="flex w-full items-center gap-2 rounded-lg bg-primary px-3 text-left text-sm font-medium text-primary-foreground transition-colors min-h-[44px] hover:bg-[hsl(var(--primary-hover))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Eye className="h-4 w-4" aria-hidden="true" />
        View store as customer
      </button>
    </div>
  );
}

/**
 * Desktop fixed sidebar (≥md only). On phones it's hidden and replaced by the
 * AdminMobileTopBar's Sheet drawer.
 */
export default function AdminSidebar() {
  return (
    <aside
      className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-sunk border-r border-border flex-col z-50"
      data-testid="admin-sidebar"
    >
      <AdminBrandHeader />
      <AdminBackToStoreLink />
      <AdminNavList />
    </aside>
  );
}
